import { type PropsWithChildren, useEffect } from 'react';

import { useQueryMyUser } from '~/data/user';

import authService from '~/services/auth.service';
import CompanyService from '~/services/company.service';
import CompanyIdentifierService from '~/services/companyIdentifier.service';
import DataExchangeService from '~/services/dataExchange.service';

// Check if logout is already in process to prevent infinite loop of page refreshes.
const shouldLogOut = () => {
  const val = new URLSearchParams(document.location.search).get('logout');

  return val !== 'true';
};

// An expired token must not count as logged in: querying with it produces a
// 401/token-refresh loop instead of letting the auth provider re-login.
const isLoggedIn = () => authService.isAccessTokenValid();

// Query all information that should be in global state.
const loadData = async () => {
  await Promise.allSettled([
    CompanyIdentifierService.loadCompanyIdentifiers(),
    CompanyService.loadSupplierAlertConfigs(),
    DataExchangeService.loadDataExchangeCompanies(),
    DataExchangeService.loadDataExchanges(),
  ]);
};

export const AppInitializer = ({ children }: PropsWithChildren) => {
  const { data: currentUser, isSuccess: isSuccessCurrentUser } = useQueryMyUser(
    {
      enabled: isLoggedIn(),
    },
  );

  useEffect(() => {
    if (!isSuccessCurrentUser) {
      return;
    }

    if (!(currentUser?.isAdmin || currentUser?.isExternalAdmin)) {
      if (shouldLogOut()) {
        void authService.logout();
      }

      return;
    }

    if (currentUser?.isAdmin) {
      void loadData();
    }
  }, [currentUser, isSuccessCurrentUser]);

  return children;
};
